import { useNavigate, useParams } from 'react-router-dom'
import { ShowBase, useRecordContext } from 'ra-core'
import { EditButton, Show } from '@internal/ui-kit/components/admin'
import { Badge } from '@internal/ui-kit/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@internal/ui-kit/components/ui/tabs'
import { AppointmentsTab } from './AppointmentsTab'
import { DangerZoneTab } from './DangerZoneTab'
import { InsuranceTab } from './InsuranceTab'
import { InventoryTab } from './InventoryTab'
import { LoyaltyTab } from './LoyaltyTab'
import { OrdersTab } from './OrdersTab'
import { ProfileTab } from './ProfileTab'

export const USER_PROFILE_TABS = [
  'profile',
  'orders',
  'events',
  'loyalty',
  'insurance',
  'inventory',
  'danger-zone',
] as const
export type UserProfileTab = (typeof USER_PROFILE_TABS)[number]

/**
 * The combined page's per-tab content (HATCH-101 round 4 — ticket feedback:
 * split the single-URL tabbed page into one URL per tab, `/users/:id/<tab>`,
 * so each tab is independently deep-linkable and the page-level Edit button
 * (edits the `profile` tab's own scalar fields, see `index.tsx`'s
 * `UserEditPage`) only shows up on the tab it actually applies to — same
 * "Edit button next to what it edits" reasoning `apps/brands`'
 * `BrandDetailPage` already follows.
 */
function UserProfileContent({ tab }: { tab: UserProfileTab }) {
  const record = useRecordContext()
  const navigate = useNavigate()
  if (!record) return null
  const userId = String(record.user_id ?? record.id)

  return (
    <Tabs value={tab} onValueChange={(value) => navigate(`/users/${userId}/${value}`)}>
      <TabsList>
        <TabsTrigger value="profile">Profile</TabsTrigger>
        <TabsTrigger value="orders">Orders</TabsTrigger>
        <TabsTrigger value="events">Appointments</TabsTrigger>
        <TabsTrigger value="loyalty">Loyalty</TabsTrigger>
        <TabsTrigger value="insurance">Insurance</TabsTrigger>
        <TabsTrigger value="inventory">Inventory</TabsTrigger>
        <TabsTrigger value="danger-zone">Danger zone</TabsTrigger>
      </TabsList>
      <TabsContent value="profile">
        <ProfileTab />
      </TabsContent>
      <TabsContent value="orders">
        <OrdersTab userId={userId} />
      </TabsContent>
      <TabsContent value="events">
        <AppointmentsTab userId={userId} />
      </TabsContent>
      <TabsContent value="loyalty">
        <LoyaltyTab userId={userId} />
      </TabsContent>
      <TabsContent value="insurance">
        <InsuranceTab userId={userId} />
      </TabsContent>
      <TabsContent value="inventory">
        <InventoryTab userId={userId} />
      </TabsContent>
      <TabsContent value="danger-zone">
        <DangerZoneTab userId={userId} email={record.email} />
      </TabsContent>
    </Tabs>
  )
}

/**
 * The page header (round 5 feedback: "change header of page (use first/last
 * name/email/scopes badges), user_id as smaller subtitle text" — replacing
 * `Show`'s own default title, which just showed the raw record
 * representation).
 */
function UserProfileHeader() {
  const record = useRecordContext()
  if (!record) return null
  const fullName = [record.first_name, record.last_name].filter(Boolean).join(' ')

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span>{fullName || record.email || 'User'}</span>
        {fullName && record.email && (
          <span className="text-muted-foreground text-base font-normal">{record.email}</span>
        )}
        {((record.scopes as string[]) ?? []).map((scope) => (
          <Badge key={scope} variant="outline">
            {scope}
          </Badge>
        ))}
      </div>
      <span className="text-muted-foreground text-sm font-normal">{record.user_id ?? record.id}</span>
    </div>
  )
}

/**
 * The combined user page (HATCH-101, `migration-manifest.yaml`'s merge
 * decision — PR #52 review, alexander-gridnev: `user-profile` and
 * `user-detail` land as one page, not two separate screens). Round 4 (ticket
 * feedback) moved it off the `users` resource's own `:id/show` route onto
 * one `CustomRoutes` path per tab (`App.tsx`) — `/users/:id/profile`,
 * `/users/:id/orders`, etc — each independently deep-linkable, `profile`
 * default. `ShowBase` is used standalone (not the resource's own `show`
 * route/context), same public API `apps/reviews`' own notes on
 * `/users/{user_id}/show` describe; that route still exists (see
 * `UserProfileRedirect` below) purely to redirect old links, not to render
 * this content itself anymore.
 */
export function UserProfilePage({ tab }: { tab: UserProfileTab }) {
  const { id } = useParams()
  return (
    <Show
      resource="users"
      id={id}
      title={<UserProfileHeader />}
      actions={tab === 'profile' ? <EditButton /> : <span />}
      // Appointments' own table (`AppointmentsTab`) has too many columns for
      // the shared `max-w-3xl` detail-page cap to stay readable — same
      // `fullWidth` opt-out `apps/orders`' `OrderDetailPage` already uses for
      // its own wide items/events tables (ticket feedback: "can we make
      // appoitments table full width?"). Only this tab opts out, same
      // per-tab conditional `actions` above already does — the other tabs'
      // flat field layouts still want the readable cap.
      fullWidth={tab === 'events'}
    >
      <UserProfileContent tab={tab} />
    </Show>
  )
}

/**
 * `/users/:id/show` (the `users` resource's own generated route) is kept
 * working as a redirect to the `profile` tab, since `apps/reviews/index.tsx`
 * hardcodes a link to `/users/{user_id}/show` (reviewer-confirmed, see its
 * own MIGRATION-NOTES.md) — not safe to remove even though this page's own
 * navigation no longer produces that URL.
 */
export function UserProfileRedirect() {
  return (
    <ShowBase resource="users">
      <UserProfileRedirectContent />
    </ShowBase>
  )
}

function UserProfileRedirectContent() {
  const record = useRecordContext()
  const navigate = useNavigate()
  if (!record) return null
  navigate(`/users/${record.user_id ?? record.id}/profile`, { replace: true })
  return null
}
